import isToday from 'date-fns/isToday';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import { createElement, ensureArray } from '../utils';
import { test } from '../date-tests';

export function Day(date, params, config) {
	const td = createElement('td');
	const button = createElement('button');

	let tabIndex = -1;
	let rendered = false;

	let modifiers = {
		today: false,
		hidden: false,
		outside: false,
		disabled: false,
		selected: false,
	};

	let day = { date, blur, focus, render, modifiers, element: button };

	button.setAttribute('type', 'button');
	button.addEventListener('click', handleClick);
	button.addEventListener('focus', handleFocus);
	button.addEventListener('keydown', handleKeydown);
	button.addEventListener('mouseover', handleMouseover);

	function render() {
		getModifiers();

		tabIndex = modifiers.today ? 0 : -1;

		button.setAttribute('tabindex', tabIndex);
		button.setAttribute('aria-hidden', modifiers.hidden);
		button.setAttribute('aria-disabled', modifiers.disabled);
		button.setAttribute('aria-selected', modifiers.selected);

		button.innerHTML = date.getDate();

		button.setAttribute('aria-label', Intl.DateTimeFormat('en-GB', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		}).format(date));

		td.className = getClasses().join(' ');
		button.className = config.classes.dayButton;

		if (!rendered && !modifiers.hidden) {
			rendered = true;
			td.append(button);
		}

		return td;
	}

	function getClasses() {
		const { classes: classNames } = config;
		const classes = [ classNames.day ];

		for (let [ key, value ] of Object.entries(modifiers)) {
			if (!value) {
				continue;
			}

			if (Object.prototype.hasOwnProperty.call(classNames, key)) {
				classes.push(classNames[key]);
			} else {
				classes.push(generateClassName(key));
			}
		}

		function generateClassName(key) {
			return key.replace(/([A-Z])/g, '-$1').toLowerCase();
		}

		return classes;
	}

	function getModifiers() {
		let m = { ...config.modifiers };

		m.selected = config.selected;

		m.disabled = [
			...ensureArray(config.disabled),
			date => isAfter(date, config.maxDate),
			date => isBefore(date, config.minDate),
		];

		modifiers.today = isToday(date);
		modifiers.outside = params.outside || false;
		modifiers.hidden = modifiers.outside && !config.showOutsideDays;

		for (let [ key, value ] of Object.entries(m)) {
			let { matches, start, end } = test(date, value);

			modifiers[key] = matches;
			modifiers[key + 'End'] = end;
			modifiers[key + 'Start'] = start;
		}
	}

	function handleMouseover(event) {
		let mouseover = new CustomEvent('dayMouseover', {
			bubbles: true,
			detail: { ...day, event },
		});

		event.target.dispatchEvent(mouseover);
	}

	function handleFocus(event) {
		let focus = new CustomEvent('dayFocus', {
			bubbles: true,
			detail: { ...day, event },
		});

		event.target.dispatchEvent(focus);
	}

	function handleClick(event) {
		if (modifiers.disabled) {
			return;
		}

		let click = new CustomEvent('dayClick', {
			bubbles: true,
			detail: { ...day, event },
		});

		event.target.dispatchEvent(click);
	}

	function handleKeydown(event) {
		let keydown = new CustomEvent('dayKeydown', {
			bubbles: true,
			detail: { ...day, event },
		});

		event.target.dispatchEvent(keydown);
	}

	function focus() {
		button.focus();
		button.setAttribute('tabindex', 0);
	}

	function blur() {
		button.blur();
		button.setAttribute('tabindex', -1);
	}

	return day;
}
